import { useState } from 'react';
import { CheckCircle, XCircle, Eye, Clock, AlertCircle, FileText, FolderKanban, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const ReviewApprovals = () => {
  const { isDark } = useTheme();
  const [pendingProjects] = useState([
    {
      id: 'PRJ-002',
      name: 'SkillSwap',
      tagline: 'Peer-to-peer learning marketplace',
      category: 'EdTech',
      submittedBy: 'Jane Smith',
      submittedAt: '05/11/2025',
      priority: 'high',
    },
    {
      id: 'PRJ-003',
      name: 'LocalVoice',
      tagline: 'Amplifying community concerns',
      category: 'Civic Tech',
      submittedBy: 'Mike Johnson',
      submittedAt: '08/11/2025',
      priority: 'medium',
    },
    {
      id: 'PRJ-005',
      name: 'FoodBridge',
      tagline: 'Connecting restaurants with food banks',
      category: 'Social Impact',
      submittedBy: 'Sarah Lee',
      submittedAt: '10/11/2025',
      priority: 'high',
    },
  ]);

  const [pendingApplications] = useState([
    {
      id: 'APP-001',
      applicantName: 'Amit Kumar Singh',
      projectName: 'EcoTrack',
      role: 'Developer',
      appliedAt: '10/11/2025',
      priority: 'high',
    },
    {
      id: 'APP-004',
      applicantName: 'Sneha Patel',
      projectName: 'EcoTrack',
      role: 'Developer',
      appliedAt: '11/11/2025',
      priority: 'medium',
    },
    {
      id: 'APP-005',
      applicantName: 'Rajesh Kumar',
      projectName: 'SkillSwap',
      role: 'Designer',
      appliedAt: '12/11/2025',
      priority: 'low',
    },
  ]);

  const [pendingProblemStatements] = useState([
    {
      id: 'PS-003',
      title: 'Digital Literacy for Elderly',
      week: 'Week 3, Nov 2025',
      createdBy: 'Admin User',
      createdAt: '08/11/2025',
      status: 'Draft',
      priority: 'high',
    },
    {
      id: 'PS-007',
      title: 'Sustainable Transportation Solutions',
      week: 'Week 5, Nov 2025',
      createdBy: 'Core Head',
      createdAt: '12/11/2025',
      status: 'Draft',
      priority: 'medium',
    },
  ]);

  const handleApprove = (type, id) => {
    // TODO: Implement approval
    console.log(`Approving ${type}:`, id);
  };

  const handleReject = (type, id) => {
    // TODO: Implement rejection
    console.log(`Rejecting ${type}:`, id);
  };

  const getPriorityColor = (priority) => {
    if (isDark) {
      switch (priority) {
        case 'high':
          return 'bg-orange-500/30 text-orange-200';
        case 'medium':
          return 'bg-yellow-500/30 text-yellow-200';
        case 'low':
          return 'bg-green-500/30 text-green-200';
        default:
          return 'bg-gray-500/30 text-gray-300';
      }
    } else {
      switch (priority) {
        case 'high':
          return 'bg-red-100 text-red-700';
        case 'medium':
          return 'bg-yellow-100 text-yellow-700';
        case 'low':
          return 'bg-green-100 text-green-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    }
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 transition-colors ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>Review & Approvals</h2>
        <p className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Centralized dashboard for reviewing and approving submissions</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-3 sm:p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending Projects</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600">{pendingProjects.length}</p>
            </div>
            <FolderKanban className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
          </div>
        </div>
        <div className={`rounded-lg p-3 sm:p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending Applications</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{pendingApplications.length}</p>
            </div>
            <UserCheck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" />
          </div>
        </div>
        <div className={`rounded-lg p-3 sm:p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Draft Problem Statements</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600">{pendingProblemStatements.length}</p>
            </div>
            <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 flex-shrink-0" />
          </div>
        </div>
        <div className={`rounded-lg p-3 sm:p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Pending</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600">
                {pendingProjects.length + pendingApplications.length + pendingProblemStatements.length}
              </p>
            </div>
            <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* Review Tabs */}
      <Tabs defaultValue="projects" className="w-full space-y-4">
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <TabsList className={`inline-flex w-full sm:w-auto min-w-full sm:min-w-0 ${
            isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : ''
          }`}>
            <TabsTrigger 
              value="projects"
              className={`flex-shrink-0 ${isDark ? 'data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=inactive]:text-purple-200' : ''}`}
            >
              <span className="whitespace-nowrap">Projects ({pendingProjects.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="applications"
              className={`flex-shrink-0 ${isDark ? 'data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=inactive]:text-purple-200' : ''}`}
            >
              <span className="whitespace-nowrap">Applications ({pendingApplications.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="problem-statements"
              className={`flex-shrink-0 ${isDark ? 'data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=inactive]:text-purple-200' : ''}`}
            >
              <span className="whitespace-nowrap">Problem Statements ({pendingProblemStatements.length})</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Pending Projects Tab */}
        <TabsContent value="projects" className="mt-4 space-y-4 w-full">
          <div className={`rounded-xl shadow-md border overflow-hidden transition-colors w-full ${
            isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-3 sm:p-4 border-b transition-colors ${
              isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h3 className={`text-sm sm:text-base font-semibold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Projects Awaiting Approval</h3>
                <Link to="/admin/projects" className="w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className={`w-full sm:w-auto ${isDark ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' : ''}`}
                  >
                    View All Projects
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>PROJECT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>SUBMITTED BY</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>SUBMITTED AT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>PRIORITY</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingProjects.map((project) => (
                    <tr
                      key={project.id}
                      className={`border-b transition-colors group ${
                        isDark 
                          ? 'border-purple-500/20 hover:bg-purple-500/10' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <TableCell>
                        <div>
                          <p className={`font-medium transition-colors ${
                            isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                          }`}>{project.name}</p>
                          <p className={`text-xs transition-colors ${
                            isDark ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-500'
                          }`}>{project.tagline}</p>
                          <span className={`text-xs transition-colors ${
                            isDark ? 'text-gray-500 group-hover:text-gray-400' : 'text-gray-400'
                          }`}>{project.category}</span>
                        </div>
                      </TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.submittedBy}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.submittedAt}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                          {project.priority}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                            isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                          }`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${
                              isDark 
                                ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                                : 'text-green-600'
                            }`}
                            onClick={() => handleApprove('project', project.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${
                              isDark 
                                ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                                : 'text-red-600'
                            }`}
                            onClick={() => handleReject('project', project.id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </tr>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Card View for Projects */}
            <div className="md:hidden divide-y divide-purple-500/20 p-2 sm:p-3">
              {pendingProjects.map((project) => (
                <div key={project.id} className={`py-3 transition-colors ${
                  isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
                }`}>
                  <div className="mb-2">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className={`text-sm font-semibold transition-colors break-words leading-tight flex-1 min-w-0 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{project.name}</h3>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Button variant="ghost" size="sm" className={`h-5 w-5 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Eye className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 w-5 p-0 ${
                            isDark 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                              : 'text-green-600'
                          }`}
                          onClick={() => handleApprove('project', project.id)}
                        >
                          <CheckCircle className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 w-5 p-0 ${
                            isDark 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                              : 'text-red-600'
                          }`}
                          onClick={() => handleReject('project', project.id)}
                        >
                          <XCircle className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                    </div>
                    <p className={`text-[10px] mb-1.5 transition-colors break-words leading-tight ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{project.tagline}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] transition-colors ${
                        isDark ? 'text-gray-500' : 'text-gray-400'
                      }`}>{project.category}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${getPriorityColor(project.priority)}`}>
                        {project.priority}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div className="break-words">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Submitted by: </span>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{project.submittedBy}</span>
                    </div>
                    <div className="break-words">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Date: </span>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{project.submittedAt}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Pending Applications Tab */}
        <TabsContent value="applications" className="mt-4 space-y-4 w-full">
          <div className={`rounded-xl shadow-md border overflow-hidden transition-colors w-full ${
            isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-3 sm:p-4 border-b transition-colors ${
              isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h3 className={`text-sm sm:text-base font-semibold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Applications Awaiting Review</h3>
                <Link to="/admin/applications" className="w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className={`w-full sm:w-auto ${isDark ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' : ''}`}
                  >
                    View All Applications
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>APPLICANT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>PROJECT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>ROLE</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>APPLIED AT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>PRIORITY</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingApplications.map((app) => (
                    <tr
                      key={app.id}
                      className={`border-b transition-colors group ${
                        isDark 
                          ? 'border-purple-500/20 hover:bg-purple-500/10' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <TableCell className={`font-medium transition-colors ${
                        isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                      }`}>{app.applicantName}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.projectName}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.role}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.appliedAt}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(app.priority)}`}>
                          {app.priority}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                            isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                          }`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${
                              isDark 
                                ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                                : 'text-green-600'
                            }`}
                            onClick={() => handleApprove('application', app.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${
                              isDark 
                                ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                                : 'text-red-600'
                            }`}
                            onClick={() => handleReject('application', app.id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </tr>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Card View for Applications */}
            <div className="md:hidden divide-y divide-purple-500/20 p-2 sm:p-3">
              {pendingApplications.map((app) => (
                <div key={app.id} className={`py-3 transition-colors ${
                  isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
                }`}>
                  <div className="mb-2">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className={`text-sm font-semibold transition-colors break-words leading-tight flex-1 min-w-0 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{app.applicantName}</h3>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Button variant="ghost" size="sm" className={`h-5 w-5 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Eye className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 w-5 p-0 ${
                            isDark 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                              : 'text-green-600'
                          }`}
                          onClick={() => handleApprove('application', app.id)}
                        >
                          <CheckCircle className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 w-5 p-0 ${
                            isDark 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                              : 'text-red-600'
                          }`}
                          onClick={() => handleReject('application', app.id)}
                        >
                          <XCircle className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                      <span className={`text-[10px] transition-colors break-words ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>{app.projectName}</span>
                      <span className={`text-[10px] transition-colors ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>• {app.role}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${getPriorityColor(app.priority)}`}>
                        {app.priority}
                      </span>
                    </div>
                  </div>
                  <div className="text-[10px] break-words">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Applied: </span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{app.appliedAt}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Pending Problem Statements Tab */}
        <TabsContent value="problem-statements" className="mt-4 space-y-4 w-full">
          <div className={`rounded-xl shadow-md border overflow-hidden transition-colors w-full ${
            isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-3 sm:p-4 border-b transition-colors ${
              isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h3 className={`text-sm sm:text-base font-semibold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Problem Statements Awaiting Publication</h3>
                <Link to="/admin/problem-statements" className="w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className={`w-full sm:w-auto ${isDark ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' : ''}`}
                  >
                    View All Problem Statements
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>TITLE</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>WEEK</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>CREATED BY</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>CREATED AT</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>STATUS</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>PRIORITY</TableHead>
                    <TableHead className={`font-semibold transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingProblemStatements.map((ps) => (
                    <tr
                      key={ps.id}
                      className={`border-b transition-colors group ${
                        isDark 
                          ? 'border-purple-500/20 hover:bg-purple-500/10' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <TableCell className={`font-medium transition-colors ${
                        isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                      }`}>{ps.title}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.week}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.createdBy}</TableCell>
                      <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.createdAt}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isDark 
                            ? 'bg-gray-500/30 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {ps.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ps.priority)}`}>
                          {ps.priority}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                            isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                          }`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 px-3 ${
                              isDark 
                                ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                                : 'text-green-600'
                            }`}
                            onClick={() => handleApprove('problem-statement', ps.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Publish
                          </Button>
                        </div>
                      </TableCell>
                    </tr>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Card View for Problem Statements */}
            <div className="md:hidden divide-y divide-purple-500/20 p-2 sm:p-3">
              {pendingProblemStatements.map((ps) => (
                <div key={ps.id} className={`py-3 transition-colors ${
                  isDark ? 'hover:bg-purple-500/10' : 'hover:bg-gray-50'
                }`}>
                  <div className="mb-2">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className={`text-sm font-semibold transition-colors break-words leading-tight flex-1 min-w-0 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{ps.title}</h3>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Button variant="ghost" size="sm" className={`h-5 w-5 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Eye className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-5 px-1.5 text-[9px] whitespace-nowrap ${
                            isDark 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                              : 'text-green-600'
                          }`}
                          onClick={() => handleApprove('problem-statement', ps.id)}
                        >
                          <CheckCircle className="w-2 h-2 mr-0.5" />
                          Publish
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                      <span className={`text-[10px] transition-colors break-words ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>{ps.week}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                        isDark 
                          ? 'bg-gray-500/30 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {ps.status}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${getPriorityColor(ps.priority)}`}>
                        {ps.priority}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div className="break-words">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Created by: </span>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{ps.createdBy}</span>
                    </div>
                    <div className="break-words">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Date: </span>
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{ps.createdAt}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReviewApprovals;

